import { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Plus, Trash2 } from 'lucide-react';
import { Button, Badge, Modal, Input, Select } from '@/components/ui';
import { resourceService } from '@/services/resourceService';
import { procedureTypeService } from '@/services/procedureTypeService';
import type {
  ResourceType,
  ResourceSchedule,
  CreateScheduleRequest,
  CreateExceptionRequest,
} from '@/types';
import styles from './ResourceDetailPage.module.css';

const DAY_OF_WEEK_LABELS: Record<number, string> = {
  0: 'Domingo',
  1: 'Segunda-feira',
  2: 'Terça-feira',
  3: 'Quarta-feira',
  4: 'Quinta-feira',
  5: 'Sexta-feira',
  6: 'Sábado',
};

const DAY_OF_WEEK_OPTIONS = [
  { value: '1', label: 'Segunda-feira' },
  { value: '2', label: 'Terça-feira' },
  { value: '3', label: 'Quarta-feira' },
  { value: '4', label: 'Quinta-feira' },
  { value: '5', label: 'Sexta-feira' },
  { value: '6', label: 'Sábado' },
  { value: '0', label: 'Domingo' },
];

const EXCEPTION_TYPE_OPTIONS = [
  { value: 'BLOCK', label: 'Bloqueio total' },
  { value: 'PARTIAL_BLOCK', label: 'Bloqueio parcial' },
  { value: 'OVERRIDE', label: 'Horário especial' },
];

const RESOURCE_TYPE_LABELS: Record<ResourceType, string> = {
  DOCTOR: 'Médico',
  ROOM: 'Sala',
  EQUIPMENT: 'Equipamento',
};

/**
 * Resource detail page showing resource info, weekly schedule, exceptions,
 * and linked procedure types.
 *
 * Uses TanStack Query for all data fetching and mutations.
 * Fully accessible with semantic HTML and ARIA attributes.
 */
export function ResourceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Modal states
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [isExceptionModalOpen, setIsExceptionModalOpen] = useState(false);
  const [isLinkProcedureModalOpen, setIsLinkProcedureModalOpen] = useState(false);

  // Schedule form state
  const [scheduleDayOfWeek, setScheduleDayOfWeek] = useState('1');
  const [scheduleStartTime, setScheduleStartTime] = useState('08:00');
  const [scheduleEndTime, setScheduleEndTime] = useState('17:00');
  const [scheduleSlotDuration, setScheduleSlotDuration] = useState('30');
  const [scheduleError, setScheduleError] = useState<string | null>(null);

  // Exception form state
  const [exceptionDate, setExceptionDate] = useState('');
  const [exceptionType, setExceptionType] = useState<'BLOCK' | 'PARTIAL_BLOCK' | 'OVERRIDE'>(
    'BLOCK',
  );
  const [exceptionStartTime, setExceptionStartTime] = useState('');
  const [exceptionEndTime, setExceptionEndTime] = useState('');
  const [exceptionReason, setExceptionReason] = useState('');
  const [exceptionError, setExceptionError] = useState<string | null>(null);

  // Link procedure state
  const [selectedProcedureId, setSelectedProcedureId] = useState('');
  const [linkError, setLinkError] = useState<string | null>(null);

  const resourceId = id ?? '';

  const {
    data: resource,
    isLoading: isResourceLoading,
    error: resourceError,
  } = useQuery({
    queryKey: ['resources', resourceId],
    queryFn: () => resourceService.getResource(resourceId),
    enabled: Boolean(resourceId),
  });

  const { data: exceptions = [], isLoading: isExceptionsLoading } = useQuery({
    queryKey: ['resources', resourceId, 'exceptions'],
    queryFn: () => resourceService.getExceptions(resourceId),
    enabled: Boolean(resourceId),
  });

  const { data: linkedProcedures = [], isLoading: isProceduresLoading } = useQuery({
    queryKey: ['resources', resourceId, 'procedures'],
    queryFn: () => resourceService.getLinkedProcedures(resourceId),
    enabled: Boolean(resourceId),
  });

  const { data: allProcedureTypes = [] } = useQuery({
    queryKey: ['procedure-types'],
    queryFn: () => procedureTypeService.getProcedureTypes(),
    enabled: isLinkProcedureModalOpen,
  });

  // Mutations
  const addScheduleMutation = useMutation({
    mutationFn: (data: CreateScheduleRequest) =>
      resourceService.addSchedule(resourceId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['resources', resourceId] });
      closeScheduleModal();
    },
    onError: () => {
      setScheduleError('Erro ao adicionar horário. Tente novamente.');
    },
  });

  const removeScheduleMutation = useMutation({
    mutationFn: (scheduleId: string) =>
      resourceService.removeSchedule(resourceId, scheduleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['resources', resourceId] });
    },
  });

  const addExceptionMutation = useMutation({
    mutationFn: (data: CreateExceptionRequest) =>
      resourceService.addException(resourceId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['resources', resourceId, 'exceptions'],
      });
      closeExceptionModal();
    },
    onError: () => {
      setExceptionError('Erro ao adicionar bloqueio. Tente novamente.');
    },
  });

  const removeExceptionMutation = useMutation({
    mutationFn: (exceptionId: string) =>
      resourceService.removeException(resourceId, exceptionId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['resources', resourceId, 'exceptions'],
      });
    },
  });

  const linkProcedureMutation = useMutation({
    mutationFn: (procedureTypeId: string) =>
      resourceService.linkProcedure(resourceId, procedureTypeId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['resources', resourceId, 'procedures'],
      });
      closeLinkProcedureModal();
    },
    onError: () => {
      setLinkError('Erro ao vincular procedimento. Tente novamente.');
    },
  });

  const unlinkProcedureMutation = useMutation({
    mutationFn: (procedureTypeId: string) =>
      resourceService.unlinkProcedure(resourceId, procedureTypeId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['resources', resourceId, 'procedures'],
      });
    },
  });

  // Schedule modal handlers
  const openScheduleModal = useCallback(() => {
    setScheduleDayOfWeek('1');
    setScheduleStartTime('08:00');
    setScheduleEndTime('17:00');
    setScheduleSlotDuration('30');
    setScheduleError(null);
    setIsScheduleModalOpen(true);
  }, []);

  const closeScheduleModal = useCallback(() => {
    setIsScheduleModalOpen(false);
    setScheduleError(null);
  }, []);

  const handleAddSchedule = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      const duration = parseInt(scheduleSlotDuration, 10);
      if (isNaN(duration) || duration <= 0) {
        setScheduleError('Duração deve ser um número positivo.');
        return;
      }
      if (scheduleStartTime >= scheduleEndTime) {
        setScheduleError('Horário de início deve ser anterior ao fim.');
        return;
      }
      addScheduleMutation.mutate({
        dayOfWeek: parseInt(scheduleDayOfWeek, 10),
        startTime: scheduleStartTime,
        endTime: scheduleEndTime,
        slotDurationMinutes: duration,
      });
    },
    [scheduleDayOfWeek, scheduleStartTime, scheduleEndTime, scheduleSlotDuration, addScheduleMutation],
  );

  // Exception modal handlers
  const openExceptionModal = useCallback(() => {
    setExceptionDate('');
    setExceptionType('BLOCK');
    setExceptionStartTime('');
    setExceptionEndTime('');
    setExceptionReason('');
    setExceptionError(null);
    setIsExceptionModalOpen(true);
  }, []);

  const closeExceptionModal = useCallback(() => {
    setIsExceptionModalOpen(false);
    setExceptionError(null);
  }, []);

  const handleAddException = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      if (!exceptionDate) {
        setExceptionError('Data é obrigatória.');
        return;
      }
      if (exceptionType !== 'BLOCK' && (!exceptionStartTime || !exceptionEndTime)) {
        setExceptionError('Horários são obrigatórios para bloqueio parcial e horário especial.');
        return;
      }
      const payload: CreateExceptionRequest = {
        date: exceptionDate,
        exceptionType,
        startTime: exceptionType === 'BLOCK' ? null : exceptionStartTime || null,
        endTime: exceptionType === 'BLOCK' ? null : exceptionEndTime || null,
        reason: exceptionReason.trim() || null,
      };
      addExceptionMutation.mutate(payload);
    },
    [exceptionDate, exceptionType, exceptionStartTime, exceptionEndTime, exceptionReason, addExceptionMutation],
  );

  // Link procedure modal handlers
  const openLinkProcedureModal = useCallback(() => {
    setSelectedProcedureId('');
    setLinkError(null);
    setIsLinkProcedureModalOpen(true);
  }, []);

  const closeLinkProcedureModal = useCallback(() => {
    setIsLinkProcedureModalOpen(false);
    setLinkError(null);
  }, []);

  const handleLinkProcedure = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      if (!selectedProcedureId) {
        setLinkError('Selecione um procedimento.');
        return;
      }
      linkProcedureMutation.mutate(selectedProcedureId);
    },
    [selectedProcedureId, linkProcedureMutation],
  );

  // Group schedules by day of week
  const schedulesByDay = (resource?.schedules ?? []).reduce<
    Record<number, ResourceSchedule[]>
  >((acc, schedule) => {
    const day = schedule.dayOfWeek;
    if (!acc[day]) {
      acc[day] = [];
    }
    acc[day].push(schedule);
    return acc;
  }, {});

  // Available procedure types (not yet linked)
  const linkedProcedureIds = new Set(linkedProcedures.map((p) => p.id));
  const availableProcedureTypes = allProcedureTypes.filter(
    (p) => p.isActive && !linkedProcedureIds.has(p.id),
  );

  if (isResourceLoading) {
    return (
      <div className={styles.page}>
        <div className={styles.loadingState} role="status" aria-busy="true">
          <p>Carregando recurso...</p>
        </div>
      </div>
    );
  }

  if (resourceError || !resource) {
    return (
      <div className={styles.page}>
        <div className={styles.errorState} role="alert">
          <p>Erro ao carregar recurso. Verifique se o recurso existe.</p>
          <Button variant="outline" onClick={() => navigate('/admin/resources')}>
            Voltar para Recursos
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      {/* Back navigation */}
      <Button
        variant="ghost"
        size="sm"
        leftIcon={<ArrowLeft size={16} />}
        onClick={() => navigate('/admin/resources')}
        className={styles.backButton}
      >
        Voltar
      </Button>

      {/* Resource info header */}
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>{resource.name}</h1>
          <div className={styles.meta}>
            <Badge variant="info">
              {RESOURCE_TYPE_LABELS[resource.resourceType]}
            </Badge>
            <Badge variant={resource.isActive ? 'success' : 'error'}>
              {resource.isActive ? 'Ativo' : 'Inativo'}
            </Badge>
          </div>
        </div>
      </header>

      {/* Weekly Schedule Section */}
      <section className={styles.section} aria-labelledby="schedule-heading">
        <div className={styles.sectionHeader}>
          <h2 id="schedule-heading" className={styles.sectionTitle}>
            Horários Semanais
          </h2>
          <Button
            size="sm"
            leftIcon={<Plus size={16} />}
            onClick={openScheduleModal}
          >
            Adicionar Horário
          </Button>
        </div>
        <div className={styles.scheduleGrid}>
          {[1, 2, 3, 4, 5, 6, 0].map((day) => (
            <div key={day} className={styles.dayCard}>
              <h3 className={styles.dayTitle}>{DAY_OF_WEEK_LABELS[day]}</h3>
              {schedulesByDay[day] && schedulesByDay[day].length > 0 ? (
                <ul className={styles.scheduleList} role="list">
                  {schedulesByDay[day].map((schedule) => (
                    <li key={schedule.id} className={styles.scheduleItem}>
                      <span className={styles.scheduleTime}>
                        {schedule.startTime} - {schedule.endTime}
                      </span>
                      <span className={styles.slotDuration}>
                        {schedule.slotDurationMinutes}min
                      </span>
                      <button
                        type="button"
                        className={styles.removeButton}
                        onClick={() =>
                          removeScheduleMutation.mutate(schedule.id)
                        }
                        disabled={removeScheduleMutation.isPending}
                        aria-label={`Remover horário ${schedule.startTime} - ${schedule.endTime}`}
                      >
                        <Trash2 size={14} aria-hidden="true" />
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className={styles.noSchedule}>Sem horário</p>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Exceptions Section */}
      <section className={styles.section} aria-labelledby="exceptions-heading">
        <div className={styles.sectionHeader}>
          <h2 id="exceptions-heading" className={styles.sectionTitle}>
            Bloqueios e Excecoes
          </h2>
          <Button
            size="sm"
            leftIcon={<Plus size={16} />}
            onClick={openExceptionModal}
          >
            Adicionar Bloqueio
          </Button>
        </div>
        {isExceptionsLoading ? (
          <p className={styles.loadingInline} role="status" aria-busy="true">
            Carregando exceções...
          </p>
        ) : exceptions.length === 0 ? (
          <p className={styles.emptyMessage}>Nenhum bloqueio ou exceção cadastrado.</p>
        ) : (
          <ul className={styles.exceptionList} role="list">
            {exceptions.map((exception) => (
              <li key={exception.id} className={styles.exceptionItem}>
                <div className={styles.exceptionInfo}>
                  <Badge
                    variant={
                      exception.exceptionType === 'BLOCK'
                        ? 'error'
                        : exception.exceptionType === 'PARTIAL_BLOCK'
                          ? 'warning'
                          : 'info'
                    }
                  >
                    {exception.exceptionType === 'BLOCK'
                      ? 'Bloqueio'
                      : exception.exceptionType === 'PARTIAL_BLOCK'
                        ? 'Parcial'
                        : 'Especial'}
                  </Badge>
                  <span className={styles.exceptionDate}>{exception.date}</span>
                  {exception.startTime && exception.endTime && (
                    <span className={styles.exceptionTime}>
                      {exception.startTime} - {exception.endTime}
                    </span>
                  )}
                  {exception.reason && (
                    <span className={styles.exceptionReason}>{exception.reason}</span>
                  )}
                </div>
                <button
                  type="button"
                  className={styles.removeButton}
                  onClick={() => removeExceptionMutation.mutate(exception.id)}
                  disabled={removeExceptionMutation.isPending}
                  aria-label={`Remover exceção de ${exception.date}`}
                >
                  <Trash2 size={14} aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Procedures Section */}
      <section className={styles.section} aria-labelledby="procedures-heading">
        <div className={styles.sectionHeader}>
          <h2 id="procedures-heading" className={styles.sectionTitle}>
            Procedimentos Vinculados
          </h2>
          <Button
            size="sm"
            leftIcon={<Plus size={16} />}
            onClick={openLinkProcedureModal}
          >
            Vincular Procedimento
          </Button>
        </div>
        {isProceduresLoading ? (
          <p className={styles.loadingInline} role="status" aria-busy="true">
            Carregando procedimentos...
          </p>
        ) : linkedProcedures.length === 0 ? (
          <p className={styles.emptyMessage}>Nenhum procedimento vinculado.</p>
        ) : (
          <ul className={styles.procedureList} role="list">
            {linkedProcedures.map((procedure) => (
              <li key={procedure.id} className={styles.procedureItem}>
                <div className={styles.procedureInfo}>
                  <span className={styles.procedureName}>{procedure.name}</span>
                  <span className={styles.procedureDuration}>
                    {procedure.durationMinutes}min
                  </span>
                  {procedure.requiresPreparation && (
                    <Badge variant="warning">Requer preparo</Badge>
                  )}
                </div>
                <button
                  type="button"
                  className={styles.removeButton}
                  onClick={() => unlinkProcedureMutation.mutate(procedure.id)}
                  disabled={unlinkProcedureMutation.isPending}
                  aria-label={`Desvincular procedimento ${procedure.name}`}
                >
                  <Trash2 size={14} aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Add schedule modal */}
      <Modal
        isOpen={isScheduleModalOpen}
        onClose={closeScheduleModal}
        title="Adicionar Horário"
        size="sm"
      >
        <form onSubmit={handleAddSchedule} className={styles.form} noValidate>
          <Select
            label="Dia da semana"
            options={DAY_OF_WEEK_OPTIONS}
            value={scheduleDayOfWeek}
            onChange={(e) => setScheduleDayOfWeek(e.target.value)}
            fullWidth
          />
          <Input
            label="Horário de início"
            type="time"
            value={scheduleStartTime}
            onChange={(e) => setScheduleStartTime(e.target.value)}
            required
            fullWidth
          />
          <Input
            label="Horário de fim"
            type="time"
            value={scheduleEndTime}
            onChange={(e) => setScheduleEndTime(e.target.value)}
            required
            fullWidth
          />
          <Input
            label="Duração do slot (minutos)"
            type="number"
            value={scheduleSlotDuration}
            onChange={(e) => setScheduleSlotDuration(e.target.value)}
            min={5}
            max={120}
            required
            fullWidth
          />
          {scheduleError && (
            <p className={styles.formError} role="alert">
              {scheduleError}
            </p>
          )}
          <div className={styles.formActions}>
            <Button variant="ghost" type="button" onClick={closeScheduleModal}>
              Cancelar
            </Button>
            <Button type="submit" isLoading={addScheduleMutation.isPending}>
              Adicionar
            </Button>
          </div>
        </form>
      </Modal>

      {/* Add exception modal */}
      <Modal
        isOpen={isExceptionModalOpen}
        onClose={closeExceptionModal}
        title="Adicionar Bloqueio"
        size="sm"
      >
        <form onSubmit={handleAddException} className={styles.form} noValidate>
          <Input
            label="Data"
            type="date"
            value={exceptionDate}
            onChange={(e) => setExceptionDate(e.target.value)}
            required
            fullWidth
          />
          <Select
            label="Tipo"
            options={EXCEPTION_TYPE_OPTIONS}
            value={exceptionType}
            onChange={(e) =>
              setExceptionType(
                e.target.value as 'BLOCK' | 'PARTIAL_BLOCK' | 'OVERRIDE',
              )
            }
            fullWidth
          />
          {exceptionType !== 'BLOCK' && (
            <>
              <Input
                label="Horário de início"
                type="time"
                value={exceptionStartTime}
                onChange={(e) => setExceptionStartTime(e.target.value)}
                required
                fullWidth
              />
              <Input
                label="Horário de fim"
                type="time"
                value={exceptionEndTime}
                onChange={(e) => setExceptionEndTime(e.target.value)}
                required
                fullWidth
              />
            </>
          )}
          <div className={styles.textareaWrapper}>
            <label htmlFor="exception-reason" className={styles.textareaLabel}>
              Motivo (opcional)
            </label>
            <textarea
              id="exception-reason"
              className={styles.textarea}
              value={exceptionReason}
              onChange={(e) => setExceptionReason(e.target.value)}
              rows={3}
              placeholder="Motivo do bloqueio..."
            />
          </div>
          {exceptionError && (
            <p className={styles.formError} role="alert">
              {exceptionError}
            </p>
          )}
          <div className={styles.formActions}>
            <Button variant="ghost" type="button" onClick={closeExceptionModal}>
              Cancelar
            </Button>
            <Button type="submit" isLoading={addExceptionMutation.isPending}>
              Adicionar
            </Button>
          </div>
        </form>
      </Modal>

      {/* Link procedure modal */}
      <Modal
        isOpen={isLinkProcedureModalOpen}
        onClose={closeLinkProcedureModal}
        title="Vincular Procedimento"
        size="sm"
      >
        <form onSubmit={handleLinkProcedure} className={styles.form} noValidate>
          {availableProcedureTypes.length === 0 ? (
            <p className={styles.emptyMessage}>
              Todos os procedimentos já estão vinculados ou nenhum procedimento ativo.
            </p>
          ) : (
            <Select
              label="Procedimento"
              options={availableProcedureTypes.map((p) => ({
                value: p.id,
                label: `${p.name} (${p.durationMinutes}min)`,
              }))}
              value={selectedProcedureId}
              onChange={(e) => setSelectedProcedureId(e.target.value)}
              placeholder="Selecione um procedimento"
              fullWidth
            />
          )}
          {linkError && (
            <p className={styles.formError} role="alert">
              {linkError}
            </p>
          )}
          <div className={styles.formActions}>
            <Button variant="ghost" type="button" onClick={closeLinkProcedureModal}>
              Cancelar
            </Button>
            <Button
              type="submit"
              isLoading={linkProcedureMutation.isPending}
              disabled={availableProcedureTypes.length === 0}
            >
              Vincular
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
